import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { Button } from '@mui/material'
import useAxiosPrivate from '../../api/hooks/useAxiosPrivate'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { Version } from '../../utils/types/versionTypes'
import ReleaseNotesDialog from './VersionReleaseNotesDialog'

const POLL_INTERVAL_MS = 5 * 60 * 1000

const VersionChecker = () => {
  const axiosPrivate = useAxiosPrivate()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { setItem } = useLocalStorage('cl_acknowledged_version')
  const snackbarKeyRef = useRef<string | number | null>(null)
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [dialogVersion, setDialogVersion] = useState<Version | null>(null)

  const { data: activeVersion } = useQuery({
    queryKey: ['activeVersion'],
    queryFn: async () => {
      const params = new URLSearchParams({
        released: '1',
        itemsPerPage: '1',
        'order[date]': 'desc',
      })
      const response = await axiosPrivate.get(`/api/versions?${params}`)
      const versions = response.data['hydra:member'] as Version[]
      return versions[0] ?? null
    },
    retry: 0,
    refetchInterval: POLL_INTERVAL_MS,
  })

  useEffect(() => {
    return () => {
      if (snackbarKeyRef.current !== null) {
        closeSnackbar(snackbarKeyRef.current)
      }
    }
  }, [closeSnackbar])

  useEffect(() => {
    if (!activeVersion) return

    const acknowledged = JSON.parse(
      window.localStorage.getItem('cl_acknowledged_version') ?? 'null'
    )
    if (acknowledged?.id === String(activeVersion.id)) return

    // Close stale toast if a newer version has arrived
    if (snackbarKeyRef.current !== null) {
      closeSnackbar(snackbarKeyRef.current)
      snackbarKeyRef.current = null
    }

    // If the dialog is already open, update it to the latest version
    setDialogVersion(activeVersion)

    const key = enqueueSnackbar(
      `New version v${activeVersion.version} is available`,
      {
        persist: true,
        variant: 'info',
        action: () => (
          <Button
            size="small"
            variant="outlined"
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            onClick={() => {
              setDialogVersion(activeVersion)
              setDialogOpen(true)
            }}
          >
            View changes
          </Button>
        ),
      }
    )

    snackbarKeyRef.current = key
  }, [activeVersion, enqueueSnackbar, closeSnackbar])

  const handleAcknowledge = () => {
    if (!dialogVersion) return

    setItem({ id: String(dialogVersion.id), version: dialogVersion.version })

    if (snackbarKeyRef.current !== null) {
      closeSnackbar(snackbarKeyRef.current)
      snackbarKeyRef.current = null
    }

    window.location.reload()
  }

  return (
    <ReleaseNotesDialog
      version={dialogVersion}
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      onAcknowledge={handleAcknowledge}
    />
  )
}

export default VersionChecker
